import React, { useContext } from 'react'
// libs
import DOMPurify from 'dompurify'
// view components
import Resource from '../ResourceActivity'
// views
import {
  Content,
  ActivityComponent,
  Link
} from 'components/Modals/PlanItems/components/ActivitiesList/components/Activities/views'
// types
import { ILogMetadata, IPlanAction, IPlanKpi, IPlanObjective, LogEntityTypes } from 'types/graphql'
// context
import { ActivityBLContext } from 'containers/Activities/layers/BL/Activity'
import { ActivitiesListAPIContext } from 'containers/Activities/layers/API/ActivitiesList'
// utils
import get from 'lodash/get'
import moment from 'moment'
import { generateUserName } from 'utils/user'

import { getSourceByActivity, normalizeLink } from 'utils/activities'

interface IIncomingProps {
  isUpdate?: boolean
  isCreate?: boolean
  isDelete?: boolean
}

const ActivityContent: React.FunctionComponent<IIncomingProps> = ({ isUpdate, isCreate, isDelete }) => {
  //context
  const { activity } = useContext(ActivityBLContext)
  const { resourceFields } = useContext(ActivitiesListAPIContext)

  // constants
  const isEntity =
    activity.entityType === LogEntityTypes.Action ||
    activity.entityType === LogEntityTypes.Kpi ||
    activity.entityType === LogEntityTypes.Objective ||
    activity.entityType === LogEntityTypes.Plan

  const isComment = activity.entityType === LogEntityTypes.Comment
  const isImage = activity.entityType === LogEntityTypes.Image
  const isLink = activity.entityType === LogEntityTypes.Link
  const isResource = activity.entityType === LogEntityTypes.Resource
  const isDocument =
    activity.entityType === LogEntityTypes.Image &&
    !new RegExp(/\/(gif|jpg|jpeg|tiff|png|bmp|jpe|tif|svg)$/i).test(activity.metadata.fileContentType)

  const userName = generateUserName(activity.user)
  const entity: IPlanKpi | IPlanAction | IPlanObjective = getSourceByActivity(activity)

  const comment = activity.metadata.comment
  const attachment: ILogMetadata = activity.metadata
  const contentType = activity.metadata.fileContentType

  if (isResource) {
    return (
      <>
        {isCreate && 'added the resource'}
        {isUpdate && 'updated the resource'}
        {isDelete ? (
          <Content>{`"resource from ${moment(get(activity, 'metadata.resource.createdAt')).format(
            'DD.MM.YYYY h:mma zz'
          )}" was deleted by ${userName}`}</Content>
        ) : (
          <Resource isActivitiesList fields={resourceFields} />
        )}
      </>
    )
  }

  if (isCreate && isEntity) {
    return (
      <Content>
        "{entity.description}" was created by {userName}
      </Content>
    )
  }

  if (isUpdate && isEntity) {
    return (
      <Content>
        "{entity.description}" was updated by {userName}
      </Content>
    )
  }

  if (isDelete) {
    return (
      <Content>
        {contentType && contentType.toLowerCase()} "{activity.metadata.fileName}" was deleted by {userName}
      </Content>
    )
  }

  if (isComment) {
    return <Content dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comment ?? '') }} />
  }

  if (isDocument) {
    return (
      <Content>
        <span>
          added attachment{' '}
          <Link href={attachment.fileUrl} download>
            {attachment.fileName}
          </Link>
        </span>
      </Content>
    )
  }

  if (isImage) {
    return (
      <Content>
        <span>
          added attachment{' '}
          <Link href={attachment.fileUrl} target={'_blank'}>
            {attachment.fileName}
          </Link>
        </span>
        <Link href={attachment.fileUrl} target={'_blank'} style={{ width: 'fit-content' }}>
          <ActivityComponent src={attachment.fileUrl} alt={attachment.fileName} />
        </Link>
      </Content>
    )
  }

  if (isLink) {
    return (
      <Content>
        <span>
          {'added link '}
          <Link href={normalizeLink(attachment.fileUrl)} target={'_blank'}>
            {attachment.fileName || attachment.fileUrl}
          </Link>
        </span>
      </Content>
    )
  }
}

export default ActivityContent
